// CourtMate Web App
// Created by Taulant Geci

import React, { useState, useEffect } from 'react';
import { MatchGeneratorController } from './modules/MatchGeneratorController';
import { MatchPlayView } from './views/MatchPlayView';
import { PlayersView } from './views/PlayersView';
import { HistoryView } from './views/HistoryView';
import { StatisticsView } from './views/StatisticsView';
import { useTranslation } from 'react-i18next';
import './styles/App.css';
import './locales/i18n';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPeopleGroup, 
  faUsers, 
  faHistory, 
  faChartBar,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';

type TabType = 'matches' | 'players' | 'history' | 'statistics';

function App() {
  const { t, i18n } = useTranslation();
  const [controller] = useState(() => new MatchGeneratorController());
  const [activeTab, setActiveTab] = useState<TabType>('matches');

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    // Ensure German is the default language on first load
    if (i18n.language !== 'de' && i18n.language !== 'en') {
      i18n.changeLanguage('de');
    }
  }, []);

  const renderActiveView = () => {
    switch (activeTab) {
      case 'matches':
        return <MatchPlayView controller={controller} />;
      case 'players':
        return <PlayersView controller={controller} />;
      case 'history':
        return <HistoryView controller={controller} />;
      case 'statistics':
        return <StatisticsView controller={controller} />;
      default:
        return <MatchPlayView controller={controller} />;
    }
  };

  return (
    <div className="app dark-theme">
      {/* Header */}
      <header className="app-header">
        <h1>
          <div className="logo">
            <img 
              src="/images/courtmate-logo-60.png" 
              alt="CourtMate Logo" 
              className="logo-image"
            />
          </div>
          CourtMate
        </h1>
        
        {/* Language Selector */}
        <div className="language-selector">
          <button
            className={`lang-btn ${i18n.language === 'de' ? 'active' : ''}`}
            onClick={() => changeLanguage('de')}
            title="Deutsch"
          >
            DE
          </button>
          <button
            className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
            onClick={() => changeLanguage('en')}
            title="English"
          >
            EN
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {renderActiveView()}
      </main>

      {/* Tab Navigation */}
      <nav className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
          title={t('matches') as string}
        >
          <FontAwesomeIcon icon={faPeopleGroup} className="tab-icon" />
          {t('matches')}
        </button>
        <button 
          className={`tab-button ${activeTab === 'players' ? 'active' : ''}`}
          onClick={() => setActiveTab('players')}
          title={t('players') as string}
        >
          <FontAwesomeIcon icon={faUsers} className="tab-icon" />
          {t('players')}
        </button>
        <button 
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
          title={t('history') as string}
        >
          <FontAwesomeIcon icon={faHistory} className="tab-icon" />
          {t('history')}
        </button>
        <button 
          className={`tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
          onClick={() => setActiveTab('statistics')}
          title={t('statistics') as string}
        >
          <FontAwesomeIcon icon={faChartBar} className="tab-icon" />
          {t('statistics')}
        </button>
      </nav>
    </div>
  );
}

export default App; 